import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import {
  calcNights,
  formatePriceFn,
  formatPrice,
  setCookie,
} from "../../../../helper/commonFunction";
import { useLocation, useNavigate } from "react-router-dom";
import { toUrlVendor } from "../../../../helper/vendorMask";
import imagm from "../../../../assets/img/no-image-holder.png";
import {
  ArrowUpIcon,
  BedIcon,
  ChevronRightIcon,
  GlassIcon,
  LocationIcon,
  ParkIcon,
  PoolIcon,
  BeachIcon,
  ServiceIcon,
  WiFiIcon,
  DefaultHotelIcon,
  GuestIcon,
  TickWarningIcon,
  TickCircleIcon,
} from "../../../../helper/HwIcon";
import "../../../../assets/css/Hotel.css";
import { useHotelUtils } from "../../hooks/useHotelUtils";
import { HotelReviewComponent } from "./HotelReview";
import HotelGalleryPopup from "../../details/components/HotelGalleryPopup";
import { HotelSearchLoader } from "./HotelSearchLoader";
import { useMergedHotelData } from "../../hooks/useHotelDataMerger";
import HotelListItemSkeleton from "./HotelListItemSkeleton";
import { useSelector } from "react-redux";
import { useGetHotelDetails } from "../../hooks/useGetHotelDetails";
import { RoomDetailsPopup } from "../../popups/RoomDetailsPopu‎p.js";
import { hotelQueryParams } from "../../hooks/queryParamUtils";

// ─── Static helpers (defined once, never re-created) ─────────────────────────

const DefaultStaticAmenties = () => (
  <>
    <li>
      <GlassIcon /> Breakfast Available
    </li>
    <li>
      <BedIcon /> Double Bed
    </li>
    <li>
      <WiFiIcon /> Free Internet Access
    </li>
  </>
);

// Stable price-loading placeholder — no props, so one instance is enough.
const PriceSkeleton = () => (
  <div className="h4 text-right">
    <div
      className="skeleton-item"
      style={{
        width: "100px",
        height: "28px",
        backgroundColor: "#f0f0f0",
        borderRadius: "4px",
        marginLeft: "auto",
        marginBottom: "8px",
        animation: "skeleton-pulse 1.5s ease-in-out infinite",
      }}
    />
  </div>
);

const imgs = [1, 2, 3, 4, 5];

// Features like "No Balcony", "Not a family room", "maximum number of..." are
// noise — keep only the positive, guest-facing amenities for the chip row.
const NOISE_FEATURE_RE = /^(not\b|no\s|maximum number|room class)/i;

const getRoomClass = (features = []) => {
  const rc = features.find((f) => /^room class\s*:/i.test(f?.name || ""));
  return rc ? (rc.name.split(":")[1] || "").trim() : "";
};

const getAmenityChips = (features = []) =>
  features
    .map((f) => f?.name)
    .filter((name) => name && !NOISE_FEATURE_RE.test(name));

// Split guest policies into the parts we render distinctly.
const getRatePolicies = (guestPolicies = []) => {
  const cancel = guestPolicies.find((p) => p?.label === "Cancellation Policy");
  const chargeNote = guestPolicies.find(
    (p) => p?.label === "Policy" && /charged/i.test(p?.description || ""),
  );
  const bed = guestPolicies.find((p) => p?.label === "Bed");
  const cancelText = cancel?.description || "";
  return {
    isFree: /free cancellation/i.test(cancelText),
    cancelText,
    chargeHtml: chargeNote?.description || "",
    bed: bed?.description || "",
  };
};

// Pure icon picker — extracted so it doesn't re-create on every render.
const getAmenityIcon = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("rest") || n.includes("meal")) return <GlassIcon />;
  if (n.includes("internet") || n.includes("wifi") || n.includes("wi-fi"))
    return <WiFiIcon />;
  if (n.includes("pool") || n.includes("swimming")) return <PoolIcon />;
  if (n.includes("park")) return <ParkIcon />;
  if (n.includes("beach")) return <BeachIcon />;
  if (n.includes("room") || n.includes("bed")) return <BedIcon />;
  return <ServiceIcon />;
};

// ─── Component ───────────────────────────────────────────────────────────────

export const HotelListItemComponent = ({
  hotel,
  index,
  queryString,
  setHotelDetails,
  searchContext,
  rooms,
  loading,
  searchCompleted,
}) => {
  const { onSelectRoom, checkoutLoading, checkoutErr, detailError } =
    useGetHotelDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const { agentActiveCurrency, displayPrice, showVendorLogo } = useSelector(
    (state) => state.userData,
  );

  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showGalleryPopup, setShowGalleryPopup] = useState(false);
  const [additionalDataFromReviews, setAdditionalDataFromReviews] = useState(
    {},
  );
  const [openRates, setOpenRates] = useState(false);
  const [detailRate, setDetailRate] = useState(null);

  // Memoize query-param parsing — only re-parses when the URL actually changes.
  const {
    city,
    hid,
    nationality,
    roomInfo: guest,
    checkinDate,
    checkoutDate,
    residence,
  } = useMemo(() => hotelQueryParams(location), [location]);

  const extraRates = hotel?.rates ?? [];
  // De-duplicate rates the same way the details page does: one entry per
  // (room category + meal), keeping the cheapest.
  const dedupedRates = (() => {
    const map = new Map();
    for (const rate of extraRates) {
      const parts = (rate?.room_name || "").split("|");
      const namePart = rate?.room_name || "Room";
      const mealPart = parts.length > 1 ? parts.slice(1).join("|").trim() : "";
      const key = `${rate?.room_category ? ` ${rate?.room_category}` : ""} | ${mealPart ? mealPart : namePart}`.trim().toLowerCase();
      const existing = map.get(key);
      if (!existing || Number(rate?.amount) < Number(existing?.amount)) {
        map.set(key, rate);
      }
    }
    return Array.from(map.values());
  })();
  // HotelRack rates only expose the meal at listing time; RateHawk carries full policy data.
  const isHotelRack = (hotel?.cheapestVendor || hotel?.vendor) === "HOTELRACK";

  const { hotel: mergedHotel } = useMergedHotelData(
    hotel,
    additionalDataFromReviews,
    false,
  );

  // Memoize guest/night summary — only re-derives when searchContext or rooms changes.
  const { guests, nights, guestLabel } = useMemo(() => {
    const roomInfo = searchContext?.roomInfo || [];
    const sumBy = (key) =>
      roomInfo.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);
    const g = sumBy("noOfAdult") + sumBy("noOfChild") + sumBy("noOfInfant");
    const n = calcNights(
      searchContext?.checkinDate,
      searchContext?.checkoutDate,
    );
    return { guests: g, nights: n, guestLabel: `${rooms} Room/${n} Night` };
  }, [searchContext, rooms]);

  const { ratings, images } = useHotelUtils(mergedHotel);

  // ── Event handlers (all stable references) ──────────────────────────────────

  const handleAdditionalDataReceived = useCallback((data) => {
    setAdditionalDataFromReviews(data);
    setShowSkeleton(false);
  }, []);

  const handleArrowClick = useCallback(
    (e) => {
      if (!images?.length) return;
      e.preventDefault();
      e.stopPropagation();
      setShowGalleryPopup(true);
    },
    [images?.length],
  );

  const handleViewDetails = useCallback(() => {
    setShowGalleryPopup(false);
    setHotelDetails(mergedHotel);
  }, [setHotelDetails, mergedHotel]);

  const handleToggle = useCallback((e) => {
    // Collapsing a tall expanded rate list shrinks the page; without
    // re-anchoring, the scroll lands deep in the list. On close, snap
    // back to this hotel's card so the next item shows as expected.
    const groupEl = e?.currentTarget?.closest(".hw-hotel--stickyGroup");
    setOpenRates((prev) => {
      if (prev && groupEl) {
        requestAnimationFrame(() => {
          const top = groupEl.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top, behavior: "auto" });
        });
      }
      return !prev;
    });
  }, []);

  // Navigate to the hotel details page (same flow as the "Select a room" button).
  const handleSelectRoom = useCallback(() => {
    const detailsUrl = `${mergedHotel?.id}?${queryString}&vendor=${toUrlVendor(mergedHotel?.vendor)}&commonId=${mergedHotel?.commonId}&hrCityId=${mergedHotel?.hrCityId || ""}`;
    setCookie("hotelURL", JSON.stringify(detailsUrl));
    navigate(`/hotels/details/${detailsUrl}`);
  }, [mergedHotel, queryString, navigate]);

  const handleBookRate = useCallback(
    (rate) => {
      const rateVendor = rate?.vendor || hotel?.vendor || "RATEHAWK";

      // One block per vendor (same convention as the flight booking flow).
      // To onboard a new vendor, add another `else if (vendor === "X")` block.
      let selectedOption = {};
      if (rateVendor === "RATEHAWK") {
        selectedOption = {
          roomSrNo: "1",
          rateKey: [rate?.search_hash],
          fromSearch: true,
          commonId: hotel?.commonId,
          hotelCode: hotel?.hid,
          roomInfo: guest,
          checkinDate,
          checkoutDate,
          residence,
          cityId: city,
          nationalityId: nationality,
        };
      } else if (rateVendor === "HOTELRACK") {
        selectedOption = {
          fromSearch: true,
          vendor: "HOTELRACK",
          roomSrNo: rate?.roomSrNo ?? "1",
          rateKey: rate?.rateKey,
          hotelKey: rate?.hotelKey,
          tokenId: hotel?.tokenId,
          commonId: hotel?.commonId,
          hotelCode: hotel?.hotelrackCode || hotel?.code,
          roomInfo: guest,
          checkinDate,
          checkoutDate,
          residence,
          cityId: hotel?.hrCityId || city,
          nationalityId: nationality,
        };
      }

      onSelectRoom(selectedOption);
    },
    [
      hotel,
      hid,
      guest,
      checkinDate,
      checkoutDate,
      residence,
      city,
      nationality,
      onSelectRoom,
    ],
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        className={`hw-hotel--stickyGroup${openRates ? " is-expanded" : ""}`}
      >
        {showSkeleton ? (
          <HotelListItemSkeleton />
        ) : (
          <div
            className="hw-hotel--filterResult__listItem"
            data-key={index}
            key={index}
          >
            <div className="hw-hotel--filterResult__listItemLeft">
              <div className="hw-hotel--filterResult__listItemMimage">
                <div className="hw-hotel--listItemm">
                  {images?.[0] ? (
                    <img
                      src={images[0]}
                      alt="image"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <DefaultHotelIcon />
                  )}
                </div>
              </div>
              <div className="hw-hotel--filterResult__listItemTimage">
                {imgs.map((i) => (
                  <div className="item" key={i}>
                    <div className="hw-hotel--listItemt">
                      <img
                        src={images?.[i] ?? imagm}
                        alt="image"
                        loading="lazy"
                        decoding="async"
                      />
                      {i === imgs.length && (
                        <span
                          className="icon-up"
                          onClick={handleArrowClick}
                          style={{ cursor: "pointer" }}
                        >
                          <ArrowUpIcon />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hw-hotel--filterResult__listItemRight">
              <div className="hw-hotel--filterResult__listItemRightWrapper hw-align--stretchFull">
                <div className="hw-hotel--filterResult__listItemInfo">
                  <div className="hw-hotel--filterResult__review">
                    <div className="hw-hotel--stars">
                      {ratings.map((_, i) => (
                        <span key={i} className="filled">
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                  <div
                    className="hw-hotel--filterResult__title"
                    onClick={handleSelectRoom}
                  >
                    {hotel?.name}
                  </div>
                  <div className="hw-hotel--filterResult__location hw-fontWeight--500 hw-icon--div">
                    <LocationIcon /> {hotel?.address}
                  </div>
                  <a
                    className="hw-link hw-link--icon"
                    onClick={handleViewDetails}
                  >
                    View Hotel Details <ChevronRightIcon />
                  </a>
                  <ul className="hw-hotel--filterResult__iconText list-unstyled">
                    {mergedHotel?.details?.amenities?.length > 0 ? (
                      mergedHotel.details.amenities.slice(0, 3).map((a) => (
                        <li key={a?.name || a?.id}>
                          {getAmenityIcon(a?.name)}
                          {a?.name || a?.title}
                        </li>
                      ))
                    ) : (
                      <DefaultStaticAmenties />
                    )}
                  </ul>
                </div>

                <div className="hw-hotel--filterResult__listItemSelect">
                    {showVendorLogo ? (
                    <div className="hw-text14 hw-fontBold hw--vendor-img">
                      {String(hotel?.cheapestVendor || hotel?.vendor || "").toLowerCase().replace(/^./, (c) => c.toUpperCase())}
                    </div>
                  ) : null}
                  <div className="flex-shrink--null">
                    <div className="hw-rating--tool">
                      <HotelReviewComponent
                        hotel={hotel}
                        onAdditionalDataReceived={handleAdditionalDataReceived}
                        searchContext={searchContext}
                        searchCompleted={searchCompleted}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="h4 text-right">
                      {hotel?.loadingPrice ? (
                        <PriceSkeleton />
                      ) : (
                        formatePriceFn(
                          agentActiveCurrency,
                          formatPrice(hotel?.amount, true, true),
                          displayPrice,
                        )
                      )}
                    </div>
                    <div className="hw-text14 hw-fontWeight--500 text-right">
                      <div className="d-flex d-flex-column">
                        <b>{`${rooms} Room(s)`}</b>
                        <span>{nights} Night(s)</span>
                      </div>
                    </div>
                    <div className="hw-mt--10 d-flex hw-gapy10">
                      {extraRates?.length > 0 ? (
                        <button
                          className={`${loading && "disabled"} hw-select-btn w-100`}
                          disabled={loading}
                          onClick={handleToggle}
                        >
                          <span>Select a room</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{
                              transform: openRates ? "rotate(180deg)" : "none",
                              transition: "transform .2s ease",
                            }}
                          >
                            <path
                              d="M5.29289 9.29289C5.68342 8.90237 6.31658 8.90237 6.70711 9.29289L12 14.5858L17.2929 9.29289C17.6834 8.90237 18.3166 8.90237 18.7071 9.29289C19.0976 9.68342 19.0976 10.3166 18.7071 10.7071L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L5.29289 10.7071C4.90237 10.3166 4.90237 9.68342 5.29289 9.29289Z"
                              fill="#0062FF"
                            />
                          </svg>
                        </button>
                      ) : (
                        <button
                          className={`${loading && "disabled"} hw-select-btn w-100`}
                          disabled={loading}
                          onClick={handleSelectRoom}
                        >
                          Select a room
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {dedupedRates?.length > 0 && (
          <div className={`hw-rateList__reveal${openRates ? " is-open" : ""}`}>
            <div className="hw-rateList__revealInner">
              <div className="hw-rateList">
                {dedupedRates.map((rate, i) => {
                  // Room name = part before "|"; meal = part after "|".
                  const nameParts = (rate?.room_name || "").split("|");
                  const namePart = rate?.room_name || "Room";
                  const mealPart =
                    nameParts.length > 1
                      ? nameParts.slice(1).join("|").trim()
                      : "";
                  const roomTitle =
                    `${rate?.room_category ? ` ${rate?.room_category}` : ""} | ${mealPart ? mealPart : namePart}`.trim();
                  const amenities = getAmenityChips(rate?.features);
                  const visibleAmenities = amenities.slice(0, 4);
                  const extraCount = amenities.length - visibleAmenities.length;
                  const { isFree, cancelText, chargeHtml, bed } =
                    getRatePolicies(rate?.guestPolicies);

                  return (
                    <div
                      className="hw-rateList__card"
                      key={rate?.match_hash || rate?.search_hash || i}
                    >
                      <div className="hw-rateList__main">
                        <div>
                          <div className="hw-rateList__head">
                            <h4 className="hw-rateList__name">{roomTitle}</h4>
                          </div>
                          <div className="hw-rateList__meta">
                            <span>
                              <BedIcon /> {`${rooms} Room(s)`}
                            </span>
                            <span>
                              <GuestIcon /> {guests} Guest(s)
                            </span>
                            {bed && (
                              <span>
                                <BedIcon /> {bed}
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="hw-rateList__moreLink hw-link"
                            onClick={() => setDetailRate(rate)}
                          >
                            View More Details
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z"
                                fill="#0062FF"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* Cancellation policy — uncomment when design is confirmed
                        <div className={`hw-rateList__cancel ${isFree ? "is-free" : "is-nonref"}`}>
                          {isFree ? <TickCircleIcon /> : <TickWarningIcon />}
                          <div className="hw-rateList__cancelText">
                            <span className="hw-rateList__cancelMain">
                              {cancelText || "Non-refundable"}
                            </span>
                            {chargeHtml && (
                              <span
                                className="hw-rateList__cancelSub"
                                dangerouslySetInnerHTML={{ __html: chargeHtml }}
                              />
                            )}
                          </div>
                        </div> */}
                      </div>

                      <div className="hw-rateList__aside">
                        <div className="hw-rateList__price">
                          {formatePriceFn(
                            agentActiveCurrency,
                            formatPrice(rate?.amount, true, true),
                            displayPrice,
                          )}
                          <p style={{ fontSize: "16px" }}>{guestLabel}</p>
                          {(checkoutErr === rate?.search_hash ||
                            checkoutErr === rate?.rateKey?.[0]) &&
                            checkoutErr !== null && (
                              <span className="hw-p-error-cls text-right d-block">
                                Room checkout failed.
                              </span>
                            )}
                        </div>
                        <button
                          className="hw-book-btn"
                          onClick={() => handleBookRate(rate)}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="hw-rateList__viewAll">
                  <button
                    type="button"
                    className="hw-btn hw-btn--bsecondary"
                    onClick={handleSelectRoom}
                  >
                    View all rooms <ChevronRightIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <RoomDetailsPopup
        show={!!detailRate}
        compact
        onClose={() => setDetailRate(null)}
        title={detailRate?.room_category}
        features={detailRate?.features}
        guestPolicies={detailRate?.guestPolicies}
        isHotelRack={isHotelRack}
        meal={(detailRate?.room_name || "")
          .split("|")
          .slice(1)
          .join("|")
          .trim()}
        agentActiveCurrency={agentActiveCurrency}
        displayPrice={displayPrice}
      />

      <HotelGalleryPopup
        show={showGalleryPopup}
        setShow={setShowGalleryPopup}
        images={images}
        initialIndex={0}
      />

      {checkoutLoading && (
        <HotelSearchLoader
          mode="checkout"
          city={mergedHotel?.name || ""}
          inBig={checkinDate ? moment(checkinDate).format("DD MMM") : ""}
          inDow={checkinDate ? moment(checkinDate).format("dddd") : ""}
          outBig={checkoutDate ? moment(checkoutDate).format("DD MMM") : ""}
          outDow={checkoutDate ? moment(checkoutDate).format("dddd") : ""}
          nights={nights}
          rooms={rooms}
          adults={guest?.reduce((acc, r) => acc + (+r?.noOfAdult || 0), 0) || 0}
          children={guest?.reduce((acc, r) => acc + (+r?.noOfChild || 0), 0) || 0}
        />
      )}
    </>
  );
};

// ─── Memo comparator ─────────────────────────────────────────────────────────
// agentActiveCurrency comes from useSelector (not a prop) — React handles
// re-renders for store changes automatically, so it must NOT be in the comparator.

function hotelListItemAreEqual(prev, next) {
  if (
    prev.loading !== next.loading ||
    prev.searchCompleted !== next.searchCompleted ||
    prev.queryString !== next.queryString ||
    prev.rooms !== next.rooms ||
    prev.searchContext !== next.searchContext
  )
    return false;

  const ph = prev.hotel;
  const nh = next.hotel;
  return (
    ph?.code === nh?.code &&
    ph?.amount === nh?.amount &&
    ph?.cheapestVendor === nh?.cheapestVendor &&
    ph?.loadingPrice === nh?.loadingPrice &&
    ph?.rates === nh?.rates
  );
}

export const HotelListItem = React.memo(
  HotelListItemComponent,
  hotelListItemAreEqual,
);
